import { useController, useFormContext, useFormState } from "react-hook-form";
import RadioCard from "src/components/RadioCard/RadioCard";
import FormStep from "src/features/vendorSponsorshipAdmin/components/FormStep/FormStep";
import { HelpCircle } from "lucide-react";
import { ColorPickerStep } from "src/features/vendorSponsorshipAdmin/components/ColorPickerStep";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";

type AppBrandFormValues = AppBrand & { audienceGroupId: number | null };

const AppBrandingFormStepList = () => {
	const { control, setValue } = useFormContext<AppBrandFormValues>();

	const { field: textColor } = useController({
		control,
		name: "textColor"
	});

	const { field: brandName } = useController({
		control,
		name: "name",
		rules: { required: "Please fill out the required field" }
	});

	const { field: audienceGroupId } = useController({
		control,
		name: "audienceGroupId",
		defaultValue: null
	});

	const { data: adminGroups = [], isLoading: groupsLoading } = useAdminGroups();

	const { errors } = useFormState({ control });

	return (
		<ul className="flex flex-col gap-4">
			<li className="w-full">
				<FormStep
					stepNumber={1}
					title="App brand name"
					error={errors.name?.message}
				>
					<div>
						<input
							aria-label="brand name"
							className="w-full px-3 py-2 bg-white border border-border rounded-lg text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
							placeholder="App rebrand name"
							{...brandName}
						/>
					</div>
				</FormStep>
			</li>

			<li className="w-full">
				<FormStep
					stepNumber={2}
					title="Select brand colour"
					error={errors.brandColor?.message}
				>
					<ColorPickerStep
						name="brandColor"
						label="Pick a colour or enter a hex code"
					/>
				</FormStep>
			</li>

			<li className="w-full">
				<FormStep
					stepNumber={3}
					title="Text and menu colour"
					error={errors.textColor?.message}
				>
					<fieldset>
						<legend className="text-sm font-medium text-zinc-600 mb-3">
							Text and menu colour
						</legend>

						<div className="flex items-center gap-2">
							{["#000000", "#ffffff"].map((value) => {
								return (
									<RadioCard
										key={value}
										radioProps={{
											value,
											isChecked: textColor.value === value,
											onChange: (val) =>
												setValue("textColor", val, { shouldDirty: true }),
											name: "textColor"
										}}
										ariaLabel={value}
									>
										<div
											className="rounded h-10 w-[88px] border border-zinc-400 shadow-sm ring-1 ring-inset ring-black/5"
											style={{ backgroundColor: value }}
										/>
									</RadioCard>
								);
							})}
						</div>
					</fieldset>
				</FormStep>
			</li>

			<li className="w-full">
				<FormStep stepNumber={4} title="Audience group">
					<div className="flex flex-col gap-2">
						<label
							htmlFor="audience-group-select"
							className="text-sm font-medium text-zinc-600"
						>
							Apply this brand to
						</label>
						<select
							id="audience-group-select"
							aria-describedby="audience-group-help"
							className="w-full px-3 py-2 bg-white border border-border rounded-lg text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors disabled:opacity-50"
							value={audienceGroupId.value ?? ""}
							disabled={groupsLoading}
							onChange={(e) => {
								const raw = e.target.value;
								audienceGroupId.onChange(raw === "" ? null : Number(raw));
							}}
						>
							<option value="">Everyone (no restriction)</option>
							{adminGroups.map((g) => (
								<option key={g.id} value={Number(g.id)}>
									{g.name}
								</option>
							))}
						</select>
						<p id="audience-group-help" className="text-xs text-zinc-600">
							At most one group per brand. Users in this group will see this
							brand instead of the default.
						</p>
					</div>
				</FormStep>
			</li>
		</ul>
	);
};

export default AppBrandingFormStepList;
